// Library Methods
import { useCallback, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

// Components
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { SelectChangeEvent } from "@mui/material/Select";
import { useTheme } from "@mui/material/styles";
import DefaultModal from "src/components/shared/modals/DefaultModal/DefaultModal";
import SelectDropdown from "src/components/shared/inputs/SelectDropdown/SelectDropdown";

// Styles
import {
  ModalCustomCancel,
  ModalCustomConfirm,
  getSurfaceTokens,
  getRequiredErrorColor,
} from "src/styles/mui/muiStylesHelper";

// Types
import { ClinicType } from "src/services/clinics/types";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

type TransferDeviceModalType = {
  open: boolean;
  deviceAlias: string;
  onClose: () => void;
  onConfirm: (targetClinicId: string) => void;
};

const TransferDeviceModal = ({
  open,
  deviceAlias,
  onClose,
  onConfirm,
}: TransferDeviceModalType) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { mutedText } = getSurfaceTokens(theme);
  const { clinicInfo, allClinicInfo } = useClinicContext();

  const [selectedClinic, setSelectedClinic] = useState({
    displayName: "",
    value: "",
  });

  const targetClinics = useMemo(
    () =>
      (allClinicInfo ?? [])
        .filter((clinic: ClinicType) => clinic?.id !== clinicInfo?.id)
        .map((clinic: ClinicType) => ({
          displayName: clinic?.name,
          value: clinic?.id,
        }))
        .sort((a, b) => a?.displayName?.localeCompare(b?.displayName)),
    [allClinicInfo, clinicInfo?.id]
  );

  const dropdownOptions = useMemo(
    () => [
      {
        displayName: t("{{clinicName}} (current)", {
          clinicName: clinicInfo?.name,
        }),
        value: clinicInfo?.id,
        disabled: true,
      },
      ...targetClinics,
    ],
    [clinicInfo?.id, clinicInfo?.name, t, targetClinics]
  );

  const handleSetClinic = useCallback(
    (event: SelectChangeEvent<any>) => {
      const value = targetClinics?.find(
        (option) => option.displayName === event.target.value
      );

      if (value) setSelectedClinic({ ...value });
    },
    [targetClinics]
  );

  const resetValues = useCallback(
    () => setSelectedClinic({ displayName: "", value: "" }),
    []
  );

  const handleClose = useCallback(() => {
    resetValues();
    onClose();
  }, [resetValues, onClose]);

  const handleConfirm = useCallback(() => {
    const targetClinicId = selectedClinic?.value;
    resetValues();
    if (targetClinicId) onConfirm(targetClinicId);
  }, [resetValues, onConfirm, selectedClinic?.value]);

  return (
    <DefaultModal
      isOpen={open}
      onClose={handleClose}
      title={t("Transfer Device")}
      maxWidth="xs"
      modalActions={
        <Grid
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "8px",
          }}
        >
          <Button sx={ModalCustomCancel} onClick={handleClose}>
            {t("Cancel")}
          </Button>
          <Button
            sx={ModalCustomConfirm}
            disabled={!selectedClinic?.value}
            onClick={handleConfirm}
            aria-label="confirm-transfer-button"
          >
            {t("Transfer")}
          </Button>
        </Grid>
      }
    >
      <Grid display="flex" flexDirection="column" p="16px" gap="20px">
        <Box display="flex" flexDirection="column" gap="4px">
          <Typography fontSize="14px">
            <Trans
              i18nKey="Select a clinic to transfer <alias>{{deviceAlias}}</alias> to."
              values={{ deviceAlias }}
              components={{
                alias: <Box component="span" fontWeight={600} />,
              }}
            />
          </Typography>
          <Typography fontSize="12px" color={mutedText}>
            {t(
              "The device will be signed out and will reconnect to the new clinic."
            )}
          </Typography>
        </Box>

        {targetClinics?.length > 0 ? (
          <Box display="flex" flexDirection="column" gap="4px">
            <Typography fontSize="14px">
              {t("Clinic")}
              <span style={{ color: getRequiredErrorColor(theme) }}>*</span>
            </Typography>
            <SelectDropdown
              id="transfer-device-clinic-dropdown"
              required
              dropdownOptions={dropdownOptions}
              value={selectedClinic?.displayName}
              handleChange={handleSetClinic}
            />
          </Box>
        ) : (
          <Typography>{t("No other clinics available.")}</Typography>
        )}
      </Grid>
    </DefaultModal>
  );
};

export default TransferDeviceModal;
